import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { BaseChartProps } from "./TemperatureCableChart";
import { binDBKeys } from "src/pages/binOverview/BinCommander";
import { useCallback, useState } from "react";
import { RangeValue } from "./ChartContainer";
import dayjs from "dayjs";
import { Button, Row, Select, SelectProps, Space } from "antd";
import { ChartDatePicker } from "./Co2Chart";
import React from "react";
import { AmbientPlenumChart, LayerDewPointChart, useAmbientPlenumTemperatureQueryOptions, useGetLowestLayerTemperatureHistoryQueryOptions } from "./AmbientPlenumChart";
import { BaseChartQueryOptions, BaseCsv, useAddDefaultAnnotationsToCsv } from './modehistoryAndFanChangeHistory';
import { RedoOutlined } from "@ant-design/icons";
import { useModeChangeHistory } from "./modehistoryAndFanChangeHistory";
import { LayerTemperatureHistoryChart } from "./LayerTemperatureHistoryChart";
import BinApiService from "src/api/BinApiService";
import { CSVLink } from "react-csv";
import { queryOptions } from "src/queries/v4QueryOptions/queryOptions";
import { useFanHeaterChangeHistoryQueryOptions } from "./shared";
import { CsvButton } from "./csv/CsvButton";
import { chartCsvHeaders, timestampHeader } from "./csv/core";
import { useGrainTypeCsv } from "./csv/useGrainTypeCsv";
import { BaseHeaders } from "./csv/types";
import { useBinDTOContext } from "src/queries/BinDTOContext";


export const IncomingAirConditionsChartTab = (props: BaseChartProps) => {
    const bindtoContext = useBinDTOContext();
    const currentDate = dayjs();
    const priorDate = currentDate.subtract(7, 'days');
  
  
    const isControlled = typeof props.value != 'undefined';
    const [internalValue, setIntervalValue] = useState<RangeValue>([priorDate, currentDate]);
    const inprogress = useIsFetching([...binDBKeys.bin(props.binId), "charts"]);
    const queryClient = useQueryClient();
  
    // Internally, we need to deal with some value. Depending on whether
    // the component is controlled or not, that value comes from its
    // props or from its internal state.
  
    let value = isControlled ? props.value : internalValue;
  
    const onDateChange = useCallback((values: RangeValue, formatter?: any) => {
      if (props.onChange) {
        props.onChange(values);
      }
      else {
        setIntervalValue(values);
      }
    }, [props.onChange]);

    const useRHForFanControl = bindtoContext.binDTO?.useRHForFanControl!;

    const ambientPlenumTemperatureChartQueryOptions = useAmbientPlenumTemperatureQueryOptions({
      binId: props.binId,
      range: value,
    });
    const ambientPlenumTemperatureChartQuery = useQuery({
      ...ambientPlenumTemperatureChartQueryOptions,
    })

      const getLowestLayerTemperatureHistoryQueryOptions = useGetLowestLayerTemperatureHistoryQueryOptions({
        binId: props.binId,
        range: value,
      })
      
      const chartLowestLayerQuery = useQuery({...getLowestLayerTemperatureHistoryQueryOptions});
    

    function flattenToCsv() {


      const coldestLayerTempHeader = "Coldest Layer Temp F";


      const headers: BaseHeaders = [timestampHeader];

      headers.push(chartCsvHeaders.fanPlenumTempF);
      headers.push(chartCsvHeaders.fanPlenumRH);
      headers.push(chartCsvHeaders.fanPlenumAH);
      headers.push(chartCsvHeaders.fanPlenumDP);
      if (useRHForFanControl === false) {
        headers.push(chartCsvHeaders.fanPlenumEMC);
      }
      
      headers.push(chartCsvHeaders.fanAmbientTempF);
      headers.push(chartCsvHeaders.fanAmbientRH);
      headers.push(chartCsvHeaders.fanAmbientAH);
      headers.push(chartCsvHeaders.fanAmbientDP)
      if (useRHForFanControl === false) {
        headers.push(chartCsvHeaders.fanAmbientEMC);
      }
      
      headers.push(coldestLayerTempHeader);

      const data: BaseCsv[] = [];

      const timeSeries = ambientPlenumTemperatureChartQuery.data?.[0]?.data?.map(p => p.x) ?? [];
      for (let i = 0; i < timeSeries.length; i++) {
        const row = {
          [timestampHeader]: timeSeries[i],
          [chartCsvHeaders.fanPlenumTempF]: ambientPlenumTemperatureChartQuery.data?.[0]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumRH]: ambientPlenumTemperatureChartQuery.data?.[4]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumAH]: ambientPlenumTemperatureChartQuery.data?.[5]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumDP]: ambientPlenumTemperatureChartQuery.data?.[3]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumEMC]: ambientPlenumTemperatureChartQuery.data?.[6]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientTempF]: ambientPlenumTemperatureChartQuery.data?.[2]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientRH]: ambientPlenumTemperatureChartQuery.data?.[7]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientAH]: ambientPlenumTemperatureChartQuery.data?.[8]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientDP]: ambientPlenumTemperatureChartQuery.data?.[9]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientEMC]: ambientPlenumTemperatureChartQuery.data?.[10]?.data?.[i]?.y,
          [coldestLayerTempHeader]: chartLowestLayerQuery.data?.[0]?.data?.[i]?.temperatureF,
        };
        data.push(row);

    }
    return {
      headers: headers, data: data
    };
  }
    const data = flattenToCsv();


    const withAnnotions = useAddDefaultAnnotationsToCsv({
      baseData: data.data,
      baseHeaders: data.headers,
      binId: props.binId,
      range: value,
    });

    const finalCsv = useGrainTypeCsv({...withAnnotions});
  
    return <>
      <div>
        <Space direction="horizontal" wrap>
          <ChartDatePicker startDate={value?.[0]!} endDate={value?.[1]!} onDateRangeChange={onDateChange} />
          <Button type="primary" disabled={inprogress > 0} icon={<RedoOutlined spin={inprogress > 0} />} onClick={() => {
            onDateChange([value?.[0]!, dayjs()], ['', '']);
          }}>
          </Button>

          <CsvButton data={finalCsv.data} headers={finalCsv.headers} baseFileName="air-conditions" />
  
        </Space>
  
        <div style={{ width: "100%" }}>
  
  
          <AmbientPlenumChart binId={props.binId} deviceId={props.deviceId} useRHForFanControl={bindtoContext.binDTO?.useRHForFanControl!} onChange={onDateChange} value={value} min={0} chartHeight={188} containerMinHeight={188 * 1} />
          <LayerDewPointChart binId={props.binId} deviceId={props.deviceId} onChange={onDateChange} value={value} chartHeight={188} />

        </div>
      </div>
    </>
}